import type { ColumnsType } from 'antd/es/table'

import { columnStateKey, definedPinOf } from '../treadmillMath'

import type { ColumnPinSide, ColumnSettingsItem, ColumnStateMap } from './types'

const PIN_RANK: Record<ColumnPinSide | 'none', number> = {
  left: 0,
  none: 1,
  right: 2
}

export type ColumnSettingsSplit = {
  shown: ColumnSettingsItem[]
  hidden: ColumnSettingsItem[]
}

export const toColumnSettingsItems = <T>(
  columns: ColumnsType<T>,
  lockedKeys: string[]
): ColumnSettingsItem[] =>
  columns.flatMap((column, position) => {
    if (typeof column.title !== 'string') {
      return []
    }

    const key = columnStateKey(column, position)
    const definedPin = definedPinOf(column)

    return [
      {
        key,
        title: column.title,
        position,
        isLocked: lockedKeys.includes(key),
        definedPin
      }
    ]
  })

export const pinSideOf = (
  item: ColumnSettingsItem,
  columnsMap: ColumnStateMap | undefined
): ColumnPinSide | undefined => {
  const state = columnsMap?.[item.key]

  return state && 'fixed' in state
    ? (state.fixed ?? undefined)
    : item.definedPin
}

const isShown = (
  item: ColumnSettingsItem,
  columnsMap: ColumnStateMap | undefined
) => item.isLocked || columnsMap?.[item.key]?.show !== false

export const splitColumnSettings = (
  items: ColumnSettingsItem[],
  columnsMap: ColumnStateMap | undefined
): ColumnSettingsSplit => {
  const orderOf = (item: ColumnSettingsItem) =>
    columnsMap?.[item.key]?.order ?? item.position
  const pinRankOf = (item: ColumnSettingsItem) =>
    PIN_RANK[pinSideOf(item, columnsMap) ?? 'none']

  return {
    shown: items
      .filter((item) => isShown(item, columnsMap))
      .sort((a, b) => pinRankOf(a) - pinRankOf(b) || orderOf(a) - orderOf(b)),
    hidden: items.filter((item) => !isShown(item, columnsMap))
  }
}

export const toColumnsMap = (
  shownKeys: string[],
  hiddenKeys: string[],
  previous: ColumnStateMap = {}
): ColumnStateMap => {
  const next: ColumnStateMap = { ...previous }

  shownKeys.forEach((key, order) => {
    next[key] = { ...previous[key], show: true, order }
  })
  hiddenKeys.forEach((key) => {
    next[key] = { ...previous[key], show: false }
  })

  return next
}

const keysOf = (items: ColumnSettingsItem[]) => items.map(({ key }) => key)

export const showColumn = (
  { shown, hidden }: ColumnSettingsSplit,
  key: string,
  previous: ColumnStateMap | undefined
) =>
  toColumnsMap(
    [...keysOf(shown), key],
    keysOf(hidden).filter((hiddenKey) => hiddenKey !== key),
    previous
  )

export const hideColumn = (
  { shown, hidden }: ColumnSettingsSplit,
  key: string,
  previous: ColumnStateMap | undefined
) =>
  toColumnsMap(
    keysOf(shown).filter((shownKey) => shownKey !== key),
    [...keysOf(hidden), key],
    previous
  )

export const keptOnHideAll = (shown: ColumnSettingsItem[], keepOne = true) => {
  const locked = shown.filter(({ isLocked }) => isLocked)

  if (locked.length) return locked

  return keepOne ? shown.slice(0, 1) : []
}

export const hideAllColumns = (
  { shown, hidden }: ColumnSettingsSplit,
  previous: ColumnStateMap | undefined,
  keepOne = true
) => {
  const kept = keptOnHideAll(shown, keepOne)

  return toColumnsMap(
    keysOf(kept),
    [
      ...keysOf(shown.filter((item) => !kept.includes(item))),
      ...keysOf(hidden)
    ],
    previous
  )
}

export const showColumns = (
  { shown, hidden }: ColumnSettingsSplit,
  keys: string[],
  previous: ColumnStateMap | undefined
) =>
  toColumnsMap(
    [...keysOf(shown), ...keys],
    keysOf(hidden).filter((key) => !keys.includes(key)),
    previous
  )

export const moveColumnsBetweenGroups = (
  items: ColumnSettingsItem[],
  shownKeys: string[],
  hiddenKeys: string[],
  previous: ColumnStateMap | undefined
): ColumnStateMap | undefined => {
  const lockedKeys = new Set(
    items.filter(({ isLocked }) => isLocked).map(({ key }) => key)
  )

  return hiddenKeys.some((key) => lockedKeys.has(key))
    ? undefined
    : toColumnsMap(shownKeys, hiddenKeys, previous)
}

export const pinColumn = (
  key: string,
  side: ColumnPinSide | undefined,
  previous: ColumnStateMap | undefined
): ColumnStateMap => ({
  ...previous,
  [key]: { ...previous?.[key], fixed: side ?? null }
})

export const withDefaultHidden = (
  columnsMap: ColumnStateMap | undefined,
  defaultHiddenKeys: string[]
): ColumnStateMap | undefined => {
  const unset = defaultHiddenKeys.filter(
    (key) => columnsMap?.[key]?.show === undefined
  )

  if (!unset.length) {
    return columnsMap
  }

  const next: ColumnStateMap = { ...columnsMap }

  unset.forEach((key) => {
    next[key] = { ...next[key], show: false }
  })

  return next
}

export const isColumnShown = (
  key: string,
  columnsMap: ColumnStateMap | undefined,
  defaultHiddenKeys: string[]
) => {
  const show = columnsMap?.[key]?.show

  return show === undefined ? !defaultHiddenKeys.includes(key) : show
}

export const pinColumnAndShow = (
  split: ColumnSettingsSplit,
  key: string,
  side: ColumnPinSide | undefined,
  previous: ColumnStateMap | undefined
): ColumnStateMap => {
  const isHidden = split.hidden.some((item) => item.key === key)
  const shownFirst =
    side && isHidden ? showColumn(split, key, previous) : previous

  return pinColumn(key, side, shownFirst)
}

export const matchesColumnSearch = (item: ColumnSettingsItem, query: string) =>
  item.title.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())
